
import React, { useEffect, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import { createPortal } from 'react-dom';
import { skillsData, SkillItem, isSkillI18nKey } from '@/config/skills';
import { useTranslation } from 'react-i18next';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { useScrollLock } from '@/hooks/useScrollLock';
import '../../styles/skillsDialog.css';

interface SkillsDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const SkillRow: React.FC<{ skill: SkillItem; displayLabel: string }> = ({ skill, displayLabel }) => (
    <div className="skills-panel-row">
        <span className="skills-panel-icon" aria-hidden="true">{skill.icon}</span>
        <span className="skills-panel-name">{displayLabel}</span>
    </div>
);

export const SkillsDialog: React.FC<SkillsDialogProps> = ({
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();
    const prefersReducedMotion = usePrefersReducedMotion();
    const titleId = useId();
    useScrollLock(isOpen);

    useEffect(() => {
        if (!isOpen) return;
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: prefersReducedMotion ? 0.15 : 0.4 }}
                        onClick={onClose}
                        aria-hidden="true"
                        className="panel-backdrop"
                    />

                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        initial={prefersReducedMotion ? { opacity: 0 } : { x: "100%" }}
                        animate={prefersReducedMotion ? { opacity: 1 } : { x: 0 }}
                        exit={prefersReducedMotion ? { opacity: 0 } : { x: "100%" }}
                        transition={{ duration: prefersReducedMotion ? 0.15 : 0.5, ease: [0.76, 0, 0.24, 1] }}
                        className="panel"
                    >
                        <div className="panel-header">
                            <h2 id={titleId} className="panel-title">
                                {t('skillsDialog.title')}
                            </h2>
                            <button
                                type="button"
                                onClick={onClose}
                                className="panel-close"
                                aria-label="Close dialog"
                            >
                                <FiX aria-hidden="true" />
                            </button>
                        </div>

                        <div className="panel-body">
                            {skillsData.map((category) => (
                                <div key={category.title} className="panel-block">
                                    <h3 className="panel-label">
                                        {isSkillI18nKey(category.title) ? t(category.title) : category.title}
                                    </h3>
                                    <div className="skills-panel-grid">
                                        {category.skills.slice().sort((a, b) => b.expertScore - a.expertScore).map((skill, skillIndex) => (
                                            <SkillRow
                                                key={`${category.title}-${skillIndex}`}
                                                skill={skill}
                                                displayLabel={isSkillI18nKey(skill.label) ? t(skill.label) : skill.label}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
};
